import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import store from 'store';

import Text from '../Text';
import { Row, Column } from '../Layout/Grid';
import { Button } from '../Button';

import UserService from 'services/user.service';
import UploadService from 'services/upload.service';

import arrowUpload from 'img/arrow-upload.svg';
import userAvatar from 'img/user-avatar.svg';
import IconInputKey from 'img/input-key.svg';
import IconSuccess from 'img/success-img.svg';

import {
  PhoneNumberOutput,
  PhoneNumberTest,
  PhoneNumberParse,
} from 'utils/PhoneNumber';
import toast from 'components/Toast/dispatch';
import Input from 'components/Input';
import PasswordValitadorBar from 'components/PasswordValitadorBar';
import Form from 'components/Form';

const UserManager: React.FC = () => {
  const _userService = new UserService();
  const _uploadService = new UploadService();
  const dispatch = useDispatch();

  const userStore = store.getState().user || {};

  const [userData, setUserData] = useState({
    ...userStore,
    phoneNumber: PhoneNumberOutput(userStore.phoneNumber || ''),
    actualPassword: '',
    password: '',
    repeatPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [avatarFileUrl, setAvatarFileUrl] = useState(
    userData?.fileUrl || userAvatar
  );

  async function addFile(e: any) {
    const fileTypes: Array<any> = ['image/jpeg', 'image/jpg', 'image/png'];
    const fileTarget: Array<any> = [...e.target.files];
    if (fileTarget.length > 0) {
      const file = fileTarget[0];
      if (fileTypes.includes(file.type)) {
        const fileUploaded = [_uploadService.upload(file)];
        setLoadingAvatar(true);
        const files = await Promise.all(fileUploaded)
          .then((fileUpload: any) =>
            fileUpload.map((item: any) => ({
              id: item.id,
              name: item.name,
              fileUrl: item.uri || item.fileUrl,
            }))
          )
          .catch(() => {
            setLoading(false);
            toast.error('Falha ao anexar arquivo(s).');
            return;
          });
        setLoadingAvatar(false);
        setUserData({
          ...userData,
          file: files[0].id,
          fileUrl: files[0].fileUrl,
        });
        setAvatarFileUrl(files[0].fileUrl || userAvatar);
      }
    }
  }

  async function editUser() {
    setErrorMessage('');

    const data = {
      id: userData.id,
      enabled: true,
      pending: userData.pending,
      fullName: userData.fullName,
      phoneNumber: userData.phoneNumber,
      file: userData.file,
      actualPassword: userData.actualPassword,
      password: userData.password,
      roles: userData.roles,
      supplierId: userData.supplierId
    };

    if (data.password && passwordStrength < 5)
      return setErrorMessage('Senha não contém requisitos mínimos');

    if (!data.fullName) return setErrorMessage('Campo nome é requerido');
    if (data.fullName.length < 5)
      return setErrorMessage(
        'Campo nome precisa conter pelo menos 5 caracteres'
      );
    if (!data.phoneNumber) return setErrorMessage('Campo telefone é requerido');
    if (!PhoneNumberTest(data.phoneNumber))
      return setErrorMessage('Digite um telefone válido');

    data.phoneNumber = PhoneNumberParse(data.phoneNumber);

    setLoading(true);

    await _userService
      .update(data)
      .then(() => {
        dispatch({ type: 'SET_USER_DATA', data: userData });
        return setSuccess(true);
      })
      .catch((err) => {
        setLoading(false);
        return setErrorMessage(
          err.response.data.data || 'Falha ao realizar requisição'
        );
      });
  }

  function onCloseSlider() {
    dispatch({ type: 'CLOSE_SLIDER' });
  }

  const isTextChangeImage = loadingAvatar ? 'Carregando...' : 'Alterar imagem';

  return !success ? (
    <>
      <Form id="registration_form" onValidate={editUser}>
        <Row className="mt-3 flex-1 flex-md-column align-md-center">
          <Column className="flex-1 mt-3 pr-2">
            <img
              src={avatarFileUrl}
              alt=""
              style={{
                padding: '10px',
                height: 150,
                width: 150,
                background: 'linear-gradient(180deg, #C4C4C4 0%, #FFFFFF 100%)',
                borderRadius: '50%',
              }}
            />
            <Row>
              <Column className="w-100 justify-center">
                <input
                  onChange={addFile}
                  id="userAvatarFile"
                  className="d-none"
                  type="file"
                  multiple
                  accept="image/jpeg, image/jpg, image/png"
                  disabled={loadingAvatar}
                />
                <label
                  className="button outline"
                  htmlFor="userAvatarFile"
                  style={{ height: 30 }}
                >
                  <img
                    src={arrowUpload}
                    alt=""
                    style={{ paddingTop: '2px', height: 14, width: 22 }}
                  />
                  <Text size={12} className="color-primary px-2">
                    {isTextChangeImage}
                  </Text>
                </label>
              </Column>
            </Row>
          </Column>
          <Column className="flex-3 pl-2">
            <Column className="mt-3">
              <Text className="color-blue-gray mb-2">Nome</Text>
              <Input
                className="w-100"
                value={userData.fullName || ''}
                onChange={(e: any) =>
                  setUserData({ ...userData, fullName: e.target.value })
                }
                type="text"
                placeholder="Nome"
                required
              />
            </Column>

            <Column className="mt-3">
              <Text className="color-blue-gray mb-2">Email</Text>
              <Input
                className="w-100"
                value={userData.email || ''}
                type="text"
                placeholder="E-mail"
                disabled
                required
              />
            </Column>

            <Column className="mt-3">
              <Text className="color-blue-gray mb-2">Telefone</Text>
              <Row className="flex-md-column">
                <Column className="flex-1 mr-2 mr-md-0">
                  <Input
                    className="w-sm-100"
                    value={userData.phoneNumber}
                    onChange={(e: any) =>
                      setUserData({
                        ...userData,
                        phoneNumber: PhoneNumberOutput(e.target.value),
                      })
                    }
                    type="text"
                    placeholder="Telefone"
                    required
                  />
                </Column>
                <Column className="flex-1 pr-5 mt-md-1">
                  <Text
                    className="color-blue-gray mb-2 text-wrap"
                    lineHeight={12}
                    size={12}
                    style={{ fontFamily: 'Roboto', fontStyle: 'italic' }}
                  >
                    Esse número será utilizado em caso de atendimento via
                    WhatsApp
                  </Text>
                </Column>
              </Row>
            </Column>
          </Column>
        </Row>
        {userStore?.userType === 'customer' && (
          <Row
            className="mt-5 mb-2"
            style={{ background: 'rgba(98, 92, 92, 0.1)' }}
          >
            <Column className="p-3 w-100">
              <Column>
                <Text
                  className="color-blue-gray font-bold pb-3"
                  lineHeight={24}
                  size={20}
                >
                  Alterar Senha
                </Text>
              </Column>
              <Column>
                <Text className="color-blue-gray mb-2">Senha Atual</Text>
                <Input
                  className="w-100 bg-white"
                  placeholder="Senha Atual"
                  type="password"
                  value={userData.actualPassword}
                  onChange={(e: any) =>
                    setUserData({ ...userData, actualPassword: e.target.value })
                  }
                  icon={IconInputKey}
                  required={userData.password !== ''}
                />
              </Column>
              <Column className="mt-1">
                <Text className="color-blue-gray mb-2">Nova Senha</Text>
                <Input
                  className="w-100 bg-white"
                  placeholder="Nova Senha"
                  type="password"
                  value={userData.password}
                  onChange={(e: any) =>
                    setUserData({ ...userData, password: e.target.value })
                  }
                  icon={IconInputKey}
                />
              </Column>
              <Column className="mt-1">
                <Text className="color-blue-gray mb-2">Confirmar Senha</Text>
                <Input
                  className="w-100 bg-white"
                  type="password"
                  value={userData.repeatPassword}
                  name="Confirmar senha"
                  compare={{ name: 'Senha', value: userData.password }}
                  placeholder="Confirmar Senha"
                  onChange={(e: any) =>
                    setUserData({ ...userData, repeatPassword: e.target.value })
                  }
                  icon={IconInputKey}
                  required={userData.password !== ''}
                />
              </Column>
              <Column className="mt-2">
                <PasswordValitadorBar
                  password={userData.password}
                  strength={(strength: number) => setPasswordStrength(strength)}
                />
              </Column>
            </Column>
          </Row>
        )}
        {errorMessage && (
          <Row className="flex-1 mt-5 align-center justify-center">
            <Text className="color-danger mt-2 animated fadeIn" size={14}>
              {errorMessage}
            </Text>
          </Row>
        )}
        <Row className="flex-1 mt-5 align-center justify-center">
          <Column>
            <Button
              form="registration_form"
              spinnerColor="#0E4C54"
              isRequesting={loading}
              disabled={loading}
              className="d-flex outline bg-primary flex-sm-1 align-center justify-center px-5"
            >
              <Text className="color-primary font-bold px-3 py-3">
                Alterar Cadastro
              </Text>
            </Button>
          </Column>
        </Row>
      </Form>
    </>
  ) : (
    <Column className="w-100 h-100 align-center">
      <img src={IconSuccess} alt="" />
      <Text className="mt-2 color-blue-gray font-bold">
        Usuário editado com sucesso
      </Text>
      <Button onClick={onCloseSlider} className="outline mt-4" width="auto">
        <Text className="color-primary font-bold px-3">Fechar</Text>
      </Button>
    </Column>
  );
};

export default UserManager;
